import { cn } from "@/lib/utils";
import {
  AlertCircle,
  ChevronRight,
  Code,
  Image,
  PenTool,
  Search,
  Wrench,
} from "lucide-react";
import PropTypes from "prop-types";
import { useState, useMemo } from "react";
import { getErrorInfo } from "../utils/errorPatternMatcher";

const ERROR_CONFIGS = {
  // AI generation errors
  safety: {
    icon: AlertCircle,
    title: "Content Policy Restriction",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30",
    textColor: "text-amber-600 dark:text-amber-400",
    friendlyMessage:
      "Your request was flagged by the model's content safety system. Try rephrasing your prompt or using a different model.",
  },
  rate_limit: {
    icon: AlertCircle,
    title: "Rate Limit Reached",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    textColor: "text-blue-600 dark:text-blue-400",
    friendlyMessage:
      "You've reached the model's rate limit. Please wait a moment and try again, or switch to a different provider.",
  },
  network: {
    icon: AlertCircle,
    title: "Connection Error",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/30",
    textColor: "text-red-600 dark:text-red-400",
    friendlyMessage:
      "Failed to connect to the model. Please check your connection and try again.",
  },
  general: {
    icon: AlertCircle,
    title: "Generation Failed",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/30",
    textColor: "text-red-600 dark:text-red-400",
    friendlyMessage: "An unexpected error occurred. Please try again.",
  },
  // Tool-specific errors
  web_search: {
    icon: Search,
    title: "Search Unavailable",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    textColor: "text-blue-600 dark:text-blue-400",
    friendlyMessage:
      "Web search is temporarily unavailable. Please try again in a moment.",
  },
  image_generation: {
    icon: Image,
    title: "Image Generation Failed",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    textColor: "text-purple-600 dark:text-purple-400",
    friendlyMessage:
      "Image generation failed. Please try again with a different prompt or model.",
  },
  code_interpreter: {
    icon: Code,
    title: "Code Execution Failed",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
    textColor: "text-orange-600 dark:text-orange-400",
    friendlyMessage:
      "Code execution failed. Please check your code and try again.",
  },
  canvas: {
    icon: PenTool,
    title: "Canvas Creation Failed",
    bgColor: "bg-teal-500/10",
    borderColor: "border-teal-500/30",
    textColor: "text-teal-600 dark:text-teal-400",
    friendlyMessage:
      "Canvas creation failed. Please try again or use a different approach.",
  },
  tool_error: {
    icon: Wrench,
    title: "Tool Execution Failed",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/30",
    textColor: "text-red-600 dark:text-red-400",
    friendlyMessage:
      "The requested tool encountered an error and could not complete. Please try again.",
  },
};

/**
 * AIErrorDisplay - Shows inline error messages based on error type
 * Used for AI generation errors (images, text responses, tool failures) with categorized error handling
 * Shows a friendly message by default with expandable technical details
 */
export function AIErrorDisplay({
  error,
  errorType = "general",
  toolName,
  className,
}) {
  const [showDetails, setShowDetails] = useState(false);
  const config = ERROR_CONFIGS[errorType] || ERROR_CONFIGS.general;
  const Icon = config.icon;

  // Get friendly message, with toolName substitution for generic tool errors
  const getFriendlyMessage = () => {
    if (errorType === "tool_error" && toolName) {
      return `The ${toolName} tool encountered an error and could not complete. Please try again.`;
    }
    return config.friendlyMessage;
  };

  // Get sanitized technical detail based on error message
  const { technicalDetail } = useMemo(() => getErrorInfo(error), [error]);

  return (
    <div
      className={cn(
        "flex flex-col space-y-2 px-4 py-3 rounded-lg",
        config.bgColor,
        config.borderColor,
        "border",
        className
      )}
    >
      {/* Header with icon and title */}
      <div className="flex items-center space-x-3">
        <Icon className={cn("w-5 h-5 flex-shrink-0", config.textColor)} />
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-medium", config.textColor)}>
            {config.title}
          </p>
        </div>
      </div>

      {/* Friendly error message */}
      <p className="text-xs text-muted-foreground pl-8">
        {getFriendlyMessage()}
      </p>

      {/* Technical details toggle - sanitized info only */}
      {error && (
        <div className="pl-8">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors"
          >
            <ChevronRight
              className={cn(
                "w-3 h-3 transition-transform duration-200",
                showDetails && "rotate-90"
              )}
            />
            <span>{showDetails ? "Hide details" : "Show details"}</span>
          </button>

          {showDetails && (
            <div className="mt-2 p-2 rounded bg-black/5 dark:bg-white/5">
              <code className="text-xs text-muted-foreground font-mono">
                {technicalDetail}
              </code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

AIErrorDisplay.propTypes = {
  /** Error message string to display */
  error: PropTypes.string,
  /** Type of error - determines styling and default message */
  errorType: PropTypes.oneOf([
    "safety",
    "rate_limit",
    "network",
    "general",
    "web_search",
    "image_generation",
    "code_interpreter",
    "canvas",
    "tool_error",
  ]),
  /** Name of the tool that failed (used in generic tool_error messages) */
  toolName: PropTypes.string,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default AIErrorDisplay;
